import { useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, IconButton, FormControlLabel, Checkbox, CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { BasicFields } from './zoneTypes/BasicFields';
import Weekdays from './zoneTypes/Weekdays';
import Holiday from './zoneTypes/Holiday';
import ConsecutiveDaysWorked from './zoneTypes/ConsecutiveDaysWorked';
import { DiferencialDaily, DiferencialWeekly } from './zoneTypes/Diferencial';
import Draggable from './common/Draggable';
import UnsavedChangesAlert from './common/UnsavedChangesAlert';
import CloseIcon from '@mui/icons-material/Close';
import { useZone } from './ZoneContext';
import { useZoneActions } from './ZoneContext';
import { useFormValidation } from './validations';
import { updateZone, getSectionByType } from './request';
import { gettingZoneById } from 'utils/Zones'
import moment from "moment";

const Edit = ({ open, setOpen, selectedRow, setSelectedRow, reloading, rows }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const zone = useZone();
    const { updateSection, resetForm, updateField } = useZoneActions();
    const { hasChanges, isDisabled } = useFormValidation();
    const [alert, setAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);


    const formComponents = () => {
        return {
            holiday: <Holiday key='holiday' open={open} />,
            week_days: <Weekdays key='week_days' />,
            consecutive_days_worked: <ConsecutiveDaysWorked key='consecutive_days_worked' open={open} />,
            diferential_daily_extend_zone_rule: <DiferencialDaily key='diferential_daily_extend_zone_rule' />,
            diferential_weekly_extend_daily: <DiferencialWeekly key='diferential_weekly_extend_daily' />,
        };
    };

    const onClose = () => {
        setOpen(false);
        resetForm();
        setAlert(false);
        setSelectedRow([]);
    }

    const changes = () => {
        hasChanges ? setAlert(true) : onClose();
    }

    const handleSave = async () => {
        setLoading(true);
        try {
            const type = getSectionByType(zone.general.type);
            const { holidays, overtimeSustitutions, ...data } = zone[type];

            console.log('data to update:', data);

            await updateZone(selectedRow[0].id, zone.general.type, zone.general, data);

            reloading();
            onClose();
            enqueueSnackbar(t('settings.zones.alerts.edit.success'), { variant: 'success' });
        } catch (error) {
            console.error('Error updating zone:', error);
            enqueueSnackbar(t('settings.zones.alerts.edit.error'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const request = async () => {

            if (open && selectedRow?.length > 0) {
                setInitialLoading(true);
                try {
                    const data = await gettingZoneById(selectedRow[0].id);
                    updateSection('general', {
                        name: data.name,
                        type: data.type,
                        requiredApproval: data.requires_approval,
                        description: data.description
                    });

                    switch (data.type) {
                        case 'holiday':
                            updateSection('holiday', {

                                selectedHolidays: data.holidays.map((holiday) => ({ id: holiday.holiday_id, name: holiday.name })),
                                definedBy: data.defined_by,
                                deductedTime: data.deducted_time,
                                hoursWorkedBelongTo: data.hours_worked_belong_to
                            });
                            break;
                        case 'week_days':
                            updateSection('weekDays', {
                                monday: data.days.includes('monday'),
                                tuesday: data.days.includes('tuesday'),
                                wednesday: data.days.includes('wednesday'),
                                thursday: data.days.includes('thursday'),
                                friday: data.days.includes('friday'),
                                saturday: data.days.includes('saturday'),
                                sunday: data.days.includes('sunday'),
                                zoneApplies: data.applies_to
                            });
                            break;
                        case 'consecutive_days_worked':
                            updateSection('consecutiveDays', {
                                selectedOvertimeSustitution: data.sustitution_overtime ? data.sustitution_overtime.map((item) => ({ id: item.id, name: item.name })) : [],
                                dayAmount: data.day_amount ? data.day_amount : 0,
                                minHours: data.min_hours ? moment(data?.min_hours, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                minHoursLastDay: data.min_hours_last_day ? moment(data.min_hours_last_day, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                typeConsecutive: data.type_consecutive,
                                resetTo: data.type_consecutive === 'consecute_days' && data.consecutive_days_numberday ? 'day_number' : 'days_week',
                                dayNumber: data.consecutive_days_numberday ? data.consecutive_days_numberday : 0,
                                weekday1: data.consecutive_days_day_week || 'monday',
                                weekday2: data.days_week_restart_day || 'monday'
                            });
                            break;
                        case 'diferential_daily_extend_zone_rule':
                            updateSection('diferentialDaily', {
                                start: data.start_hour ? moment(data.start_hour, 'HH:mm:ss') : moment(new Date().setHours(12, 0, 0, 0)),
                                end: data.end_hour ? moment(data.end_hour, 'HH:mm:ss') : moment(new Date().setHours(12, 0, 0, 0)),
                                withinZone: data.min_hours_in_zone ? moment(data.min_hours_in_zone, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                withinTurn: data.min_hours_in_shift ? moment(data.min_hours_in_shift, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                early: data.margin_early ? moment(data.margin_early, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                late: data.margin_late ? moment(data.margin_late, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                basedBy: data.based_by ? data.based_by : 'real_punch',
                                planned: data.planned ? data.planned : false,
                                beforeStart: data.start_work_before ? true : false,
                                afterStart: data.start_work_after ? true : false,
                                afterTimeStart: data.start_work_after ? moment(data.start_work_after, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                beforeTimeStart: data.start_work_before ? moment(data.start_work_before, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                beforeFinish: data.end_work_before ? true : false,
                                afterFinish: data.end_work_after ? true : false,
                                afterTimeFinish: data.end_work_after ? moment(data.end_work_after, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                beforeTimeFinish: data.end_work_before ? moment(data.end_work_before, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0))
                            });
                            break;
                        case 'diferential_weekly_extend_daily':
                            updateSection('diferentialWeekly', {
                                startWeekly: data.start_hour ? moment(data.start_hour, 'HH:mm:ss') : moment(new Date().setHours(12, 0, 0, 0)),
                                endWeekly: data.end_hour ? moment(data.end_hour, 'HH:mm:ss') : moment(new Date().setHours(12, 0, 0, 0)),
                                withinZoneWeekly: data.min_hours_in_zone ? moment(data.min_hours_in_zone, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                withinTurnWeekly: data.min_hours_in_shift ? moment(data.min_hours_in_shift, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                earlyWeekly: data.margin_early ? moment(data.margin_early, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                lateWeekly: data.margin_late ? moment(data.margin_late, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                basedByWeekly: data.based_by ? data.based_by : 'real_punch',
                                planned: data.planned ? data.planned : false,
                                beforeStartWeekly: data.start_work_before ? true : false,
                                afterStartWeekly: data.start_work_after ? true : false,
                                afterTimeStartWeekly: data.start_work_after ? moment(data.start_work_after, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                beforeTimeStartWeekly: data.start_work_before ? moment(data.start_work_before, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                beforeFinishWeekly: data.end_work_before ? true : false,
                                afterFinishWeekly: data.end_work_after ? true : false,
                                afterTimeFinishWeekly: data.end_work_after ? moment(data.end_work_after, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                beforeTimeFinishWeekly: data.end_work_before ? moment(data.end_work_before, 'HH:mm:ss') : moment(new Date().setHours(0, 0, 0, 0)),
                                weekday1Weekly: data.start_day ? data.start_day : 'monday',
                                weekday2Weekly: data.end_day ? data.end_day : 'monday'
                            });
                            break;
                        default:
                            console.warn('Unknown zone type:', data.type);
                            break;
                    }
                    // }
                } catch (error) {
                    console.error('Error fetching zone data:', error);
                } finally {
                    setInitialLoading(false);
                }
            }
        }
        request();
    }, [open, selectedRow, updateSection]);

    return (
        <Dialog
            open={open}
            fullWidth
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            maxWidth='sm'
            PaperComponent={Draggable}
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            {initialLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
                        <Typography variant='body3'>{t('settings.zones.titles.edit')}</Typography>
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => changes()}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <UnsavedChangesAlert
                        show={alert}
                        onCancel={() => setAlert(false)}
                        onConfirm={onClose}
                    />
                    <DialogContent dividers>
                        <BasicFields rows={rows} selectedRow={selectedRow} />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                {formComponents()[zone.general.type]}
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={zone.general.requiredApproval}
                                        onChange={(event) => updateField('general', 'requiredApproval', event.target.checked)}
                                    />
                                }
                                label={t('settings.zones.fields.holiday.requires_approval')}
                            />
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => onClose()} color="error" variant='contained'>
                            {t('general.buttons.cancel')}
                        </Button>
                        <Button
                            color="primary" variant='contained'
                            disabled={isDisabled}
                            onClick={handleSave}
                            loading={loading}
                        >
                            {t('general.buttons.save')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export { Edit };